import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar, Pie } from 'react-chartjs-2';
import { useTransactions } from '../contexts/TransactionContext';
import analyticsService from '../services/analyticsService';
import budgetService from '../services/budgetService';
import Icon from './Icon';
import HeatmapChart from './HeatmapChart';
import { getCategoryIcon } from '../utils/categoryIcons';
import {
  CHART_COLORS,
  CHART_TABS,
  WEEKDAY_LABELS,
  chartOptions,
  pieOptions,
} from './chartOptions';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const Dashboard = () => {
  const { transactions } = useTransactions();
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [chartType, setChartType] = useState('category');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [pickerYear, setPickerYear] = useState(new Date().getFullYear());
  const [budgets, setBudgets] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleCategoryClick = useCallback((category) => {
    setSelectedCategory(prev => prev === category ? null : category);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedCategory(null);
  }, []);

  // ESC 키로 패널 닫기
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && selectedCategory) {
        handleClosePanel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCategory, handleClosePanel]);

  const currentMonth = selectedMonth;

  const monthlyTransactions = transactions.filter(t =>
    t.date.startsWith(currentMonth)
  );

  const totalIncome = monthlyTransactions
    .filter(t => t.type === 'income')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const totalExpense = monthlyTransactions
    .filter(t => t.type === 'expense')
    .reduce((sum, t) => sum + Number(t.amount), 0);

  const balance = totalIncome - totalExpense;

  const expensesByCategory = monthlyTransactions
    .filter(t => t.type === 'expense')
    .reduce((acc, t) => {
      acc[t.category] = (acc[t.category] || 0) + Number(t.amount);
      return acc;
    }, {});

  // 월별 통계 데이터 가져오기
  useEffect(() => {
    fetchMonthlyStats();
  }, [selectedMonth]);

  useEffect(() => {
    const fetchBudgets = async () => {
      try {
        const data = await budgetService.getBudgets(selectedMonth);
        setBudgets(Array.isArray(data) ? data : []);
      } catch (error) {
        setBudgets([]);
      }
    };
    fetchBudgets();
  }, [selectedMonth]);

  const fetchMonthlyStats = async () => {
    try {
      const data = await analyticsService.getMonthlyStats(selectedMonth);
      setMonthlyStats(data);
    } catch (error) {
      // Sentry에서 처리
    }
  };

  // 요일별 평균 지출 데이터 계산 (선택 월 기준)
  const weekdayChartData = useMemo(() => {
    const weekdayTotals = [0, 0, 0, 0, 0, 0, 0]; // 월~일
    const weekdayCounts = [0, 0, 0, 0, 0, 0, 0];

    monthlyTransactions
      .filter(t => t.type === 'expense')
      .forEach(t => {
        const dayOfWeek = new Date(t.date).getDay(); // 0=일, 1=월, ...
        // 월~일 순서로 변환: 월=0, 화=1, ..., 일=6
        const index = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        weekdayTotals[index] += Number(t.amount);
        weekdayCounts[index] += 1;
      });

    const averages = weekdayTotals.map((total, i) =>
      weekdayCounts[i] > 0 ? Math.round(total / weekdayCounts[i]) : 0
    );

    return {
      labels: WEEKDAY_LABELS,
      datasets: [
        {
          label: '평균 지출',
          data: averages,
          backgroundColor: CHART_COLORS.primary,
          borderRadius: 8,
        },
      ],
    };
  }, [monthlyTransactions]);

  // 차트 데이터 설정 (내림차순 정렬)
  const sortedExpenses = Object.entries(expensesByCategory)
    .sort(([,a], [,b]) => b - a);

  const categoryChartData = {
    labels: sortedExpenses.map(([category]) => category),
    datasets: [
      {
        data: sortedExpenses.map(([,amount]) => amount),
        backgroundColor: CHART_COLORS.categories,
        borderColor: CHART_COLORS.categories,
        borderWidth: 3,
        hoverBorderWidth: 4,
        hoverBorderColor: '#FFFFFF',
      },
    ],
  };

  return (
    <div>
      {/* Hero Section */}
      <div className="card">
        <div className="card-header flex-between">
          <div>
            <h1 className="card-title">
              {new Date(selectedMonth).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long' })}
            </h1>
            <p className="card-subtitle">
              월별 재정 요약
            </p>
          </div>

          <div className="flex-center gap-8">
            <button
              className="btn-secondary btn-icon"
              onClick={() => {
                const date = new Date(selectedMonth + '-01');
                date.setMonth(date.getMonth() - 1);
                setSelectedMonth(date.toISOString().slice(0, 7));
              }}
            >
              ‹
            </button>

            <div className="date-picker-wrapper">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowDatePicker(!showDatePicker);
                  setPickerYear(new Date(selectedMonth).getFullYear());
                }}
              >
                <Icon name="calendar_today" size={16} /> {new Date(selectedMonth).toLocaleDateString('ko-KR', { year: 'numeric', month: 'short' })}
              </button>

              {showDatePicker && (
                <div className="date-picker date-picker--dropdown">
                  {/* 년도 네비게이션 */}
                  <div className="flex-between mb-16">
                    <button
                      className="date-picker-item"
                      onClick={() => setPickerYear(pickerYear - 1)}
                    >
                      ‹
                    </button>

                    <h3 className="card-title">
                      {pickerYear}년
                    </h3>

                    <button
                      className="date-picker-item"
                      onClick={() => setPickerYear(pickerYear + 1)}
                    >
                      ›
                    </button>
                  </div>

                  {/* 월 그리드 */}
                  <div className="date-picker-grid mb-16">
                    {Array.from({ length: 12 }, (_, monthIndex) => {
                      const monthValue = `${pickerYear}-${String(monthIndex + 1).padStart(2, '0')}`;
                      const isSelected = monthValue === selectedMonth;
                      const monthNames = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

                      return (
                        <button
                          key={monthValue}
                          className={`date-picker-item${isSelected ? ' date-picker-item--selected' : ''}`}
                          onClick={() => {
                            setSelectedMonth(monthValue);
                            setShowDatePicker(false);
                          }}
                        >
                          {monthNames[monthIndex]}
                        </button>
                      );
                    })}
                  </div>

                  {/* 이번 달 버튼 */}
                  <div className="flex-center date-picker__footer">
                    <button
                      className="date-picker-item"
                      onClick={() => {
                        const now = new Date();
                        setSelectedMonth(now.toISOString().slice(0, 7));
                        setPickerYear(now.getFullYear());
                        setShowDatePicker(false);
                      }}
                    >
                      이번 달
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              className="btn-secondary btn-icon"
              onClick={() => {
                const date = new Date(selectedMonth + '-01');
                date.setMonth(date.getMonth() + 1);
                setSelectedMonth(date.toISOString().slice(0, 7));
              }}
            >
              ›
            </button>
          </div>
        </div>

        <div className="summary-grid">
          <div className="summary-card income">
            <h3>수입</h3>
            <p className="amount">
              {totalIncome.toLocaleString('ko-KR')}원
            </p>
          </div>

          <div className="summary-card expense">
            <h3>지출</h3>
            <p className="amount">
              {totalExpense.toLocaleString('ko-KR')}원
            </p>
          </div>

          <div className="summary-card balance">
            <h3>잔액</h3>
            <p className="amount">
              {balance >= 0 ? '+' : ''}{balance.toLocaleString('ko-KR')}원
            </p>
          </div>
        </div>

        {totalIncome > 0 && (
          <div className="card">
            <div className="flex-between mb-12">
              <span className="card-subtitle">지출 비율</span>
              <span className="card-title">
                {((totalExpense / totalIncome) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="progress-bar">
              <div
                className={totalExpense > totalIncome ? 'progress-fill progress-fill--error' : 'progress-fill progress-fill--success'}
                style={{ width: `${Math.min((totalExpense / totalIncome) * 100, 100)}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>

      {/* 차트 섹션 */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">데이터 인사이트</h2>
          <p className="card-subtitle">지출 패턴과 트렌드를 분석해보세요</p>
        </div>

        <div className="tab-group mb-16">
          {CHART_TABS.map(tab => (
            <button
              key={tab.key}
              className={`tab-item${chartType === tab.key ? ' active' : ''}`}
              onClick={() => setChartType(tab.key)}
            >
              <Icon name={tab.icon} size={18} />
              {tab.label}
            </button>
          ))}
        </div>

        <div className="chart-container" style={{ height: '400px' }}>
          {chartType === 'category' && Object.keys(expensesByCategory).length > 0 && (
            <Pie
              data={categoryChartData}
              options={{
                ...pieOptions,
                onClick: (event, elements) => {
                  if (elements && elements.length > 0) {
                    const index = elements[0].index;
                    const category = sortedExpenses[index]?.[0];
                    if (category) handleCategoryClick(category);
                  }
                },
              }}
            />
          )}
          {chartType === 'category' && Object.keys(expensesByCategory).length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon"><Icon name="pie_chart" size={48} /></div>
              <h3 className="empty-state-title">데이터가 없습니다</h3>
              <p className="empty-state-description">거래를 추가하면 인사이트가 표시됩니다</p>
            </div>
          )}
          {chartType === 'weekday' && (
            <Bar data={weekdayChartData} options={chartOptions} />
          )}
          {chartType === 'heatmap' && (
            <HeatmapChart
              selectedMonth={selectedMonth}
              transactions={transactions}
            />
          )}
        </div>
      </div>

      {/* 카테고리 분석 */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title">지출 분석</h2>
          <p className="card-subtitle">카테고리별 지출 현황을 확인해보세요</p>
        </div>

        {Object.keys(expensesByCategory).length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon"><Icon name="shopping_bag" size={64} /></div>
            <h3 className="empty-state-title">아직 지출이 없어요</h3>
            <p className="empty-state-description">거래를 추가하면 카테고리별 분석이 표시됩니다</p>
          </div>
        ) : (
          (() => {
            const budgetMap = {};
            budgets.forEach(b => { budgetMap[b.category] = b.amount; });

            const withBudget = sortedExpenses.filter(([cat]) => budgetMap[cat]);
            const withoutBudget = sortedExpenses.filter(([cat]) => !budgetMap[cat]);

            return (
              <div className="category-analysis">
                {withBudget.length > 0 && (
                  <div className="category-analysis__section">
                    <span className="category-section-label">
                      <Icon name="check_circle" size={14} />
                      예산 설정됨
                    </span>
                    <div className="category-analysis__list">
                      {withBudget.map(([category, amount], index) => {
                        const color = CHART_COLORS.categories[index % CHART_COLORS.categories.length];
                        const budget = budgetMap[category];
                        const budgetUsage = ((amount / budget) * 100).toFixed(1);
                        const isOverBudget = amount > budget;

                        return (
                          <div
                            key={category}
                            className={`category-item category-item--clickable${selectedCategory === category ? ' category-item--active' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={`${category}: ${amount.toLocaleString('ko-KR')}원 사용, 예산 ${budget.toLocaleString('ko-KR')}원`}
                            onClick={() => handleCategoryClick(category)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCategoryClick(category); }}
                          >
                            <div
                              className="category-item__icon"
                              style={{ '--category-color': color }}
                            >
                              <Icon name={getCategoryIcon(category)} size={22} />
                            </div>
                            <div className="category-item__content">
                              <div className="category-item__header">
                                <span className="category-item__name">{category}</span>
                                <span className="category-item__budget-label">
                                  {budget.toLocaleString('ko-KR')}원
                                </span>
                              </div>
                              <div className="category-item__progress-row">
                                <div className="category-item__progress">
                                  <div
                                    className="category-item__progress-fill"
                                    style={{
                                      width: `${Math.min(parseFloat(budgetUsage), 100)}%`,
                                      '--progress-color': isOverBudget ? 'var(--color-error)' : color,
                                    }}
                                  ></div>
                                </div>
                                <span
                                  className={`category-item__percent${isOverBudget ? ' category-item__percent--over' : ''}`}
                                  style={{ '--category-color': color }}
                                >
                                  {budgetUsage}%
                                </span>
                              </div>
                              <div className="category-item__footer">
                                <span className="category-item__spent">
                                  {amount.toLocaleString('ko-KR')}원
                                </span>
                                {isOverBudget && (
                                  <span className="category-item__over-budget">
                                    초과 {(amount - budget).toLocaleString('ko-KR')}원
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {withoutBudget.length > 0 && (
                  <div className="category-analysis__section">
                    <span className="category-section-label">
                      <Icon name="info" size={14} />
                      예산 미설정
                    </span>
                    <div className="category-analysis__list">
                      {withoutBudget.map(([category, amount], index) => {
                        const colorIndex = withBudget.length + index;
                        const color = CHART_COLORS.categories[colorIndex % CHART_COLORS.categories.length];
                        const percentage = ((amount / totalExpense) * 100).toFixed(1);

                        return (
                          <div
                            key={category}
                            className={`category-item category-item--clickable${selectedCategory === category ? ' category-item--active' : ''}`}
                            role="button"
                            tabIndex={0}
                            aria-label={`${category}: ${amount.toLocaleString('ko-KR')}원, 전체 지출의 ${percentage}%`}
                            onClick={() => handleCategoryClick(category)}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleCategoryClick(category); }}
                          >
                            <div
                              className="category-item__icon"
                              style={{ '--category-color': color }}
                            >
                              <Icon name={getCategoryIcon(category)} size={22} />
                            </div>
                            <div className="category-item__content">
                              <div className="category-item__header">
                                <span className="category-item__name">{category}</span>
                                <span className="category-item__amount">
                                  {amount.toLocaleString('ko-KR')}원
                                </span>
                              </div>
                              <div className="category-item__progress-row">
                                <div className="category-item__progress">
                                  <div
                                    className="category-item__progress-fill"
                                    style={{
                                      width: `${percentage}%`,
                                      '--progress-color': color,
                                    }}
                                  ></div>
                                </div>
                                <span
                                  className="category-item__percent"
                                  style={{ '--category-color': color }}
                                >
                                  {percentage}%
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })()
        )}

        {/* 선택된 카테고리 거래 목록은 사이드 패널로 표시 */}
      </div>

      {/* 카테고리 사이드 패널 */}
      {selectedCategory && (
        <div
          className="category-panel-overlay"
          onClick={handleClosePanel}
          role="presentation"
        >
          <div
            className={`category-panel${selectedCategory ? ' category-panel--open' : ''}`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label={`${selectedCategory} 거래 내역`}
            aria-modal="true"
          >
            <div className="category-panel__header">
              <div className="category-panel__header-info">
                <div className="category-panel__header-icon">
                  <Icon name={getCategoryIcon(selectedCategory)} size={24} />
                </div>
                <h2 className="category-panel__header-title">{selectedCategory}</h2>
              </div>
              <button
                className="btn-icon btn-secondary"
                onClick={handleClosePanel}
                aria-label="패널 닫기"
              >
                <Icon name="close" size={20} />
              </button>
            </div>

            {(() => {
              const categoryTransactions = monthlyTransactions
                .filter(t => t.type === 'expense' && t.category === selectedCategory)
                .sort((a, b) => new Date(b.date) - new Date(a.date));
              const categoryTotal = categoryTransactions.reduce((sum, t) => sum + Number(t.amount), 0);

              return (
                <>
                  <div className="category-panel__summary">
                    <div className="category-panel__summary-item">
                      <span className="category-panel__summary-label">총 지출</span>
                      <span className="category-panel__summary-value">
                        {categoryTotal.toLocaleString('ko-KR')}원
                      </span>
                    </div>
                    <div className="category-panel__summary-divider"></div>
                    <div className="category-panel__summary-item">
                      <span className="category-panel__summary-label">건수</span>
                      <span className="category-panel__summary-value">
                        {categoryTransactions.length}건
                      </span>
                    </div>
                  </div>

                  <div className="category-panel__list">
                    {categoryTransactions.length > 0 ? (
                      categoryTransactions.map((t, i) => (
                        <div key={t.id || i} className="category-panel__item">
                          <div className="category-panel__item-info">
                            <span className="category-panel__item-desc">
                              {t.description || selectedCategory}
                            </span>
                            <span className="category-panel__item-date">
                              {new Date(t.date).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' })}
                            </span>
                          </div>
                          <span className="category-panel__item-amount">
                            -{Number(t.amount).toLocaleString('ko-KR')}원
                          </span>
                        </div>
                      ))
                    ) : (
                      <div className="category-panel__empty">
                        <Icon name="receipt_long" size={40} />
                        <p>이번 달 해당 카테고리 거래가 없습니다.</p>
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
